import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  MapPin,
  Briefcase,
  TrendingUp,
  Building2,
  Check,
  Mail,
} from "lucide-react";
import { AxorButton } from "@/components/primitives";
import { PrismOutline } from "@/components/Prism";
import { waLink, jobApplyMessage, mailtoLink } from "@/config";

const WhatsAppGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z" />
  </svg>
);

export const JobDetail = ({ job, onClose }) => (
  <AnimatePresence>
    {job && (
      <motion.div
        className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="job-detail-modal"
      >
        <div
          className="absolute inset-0 bg-axor-hero/80 backdrop-blur-md"
          onClick={onClose}
        />
        <motion.div
          className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-[28px] border border-white/10 bg-axor-deep sm:rounded-[28px]"
          initial={{ y: 60, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 60, opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="relative overflow-hidden border-b border-white/[0.06] bg-gradient-to-br from-axor-navy/60 to-axor-deep p-7">
            <PrismOutline className="absolute -right-4 -top-6 opacity-30" size={140} />
            <button
              onClick={onClose}
              className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/[0.04] text-axor-white transition-colors hover:bg-white/10"
              aria-label="Close"
              data-testid="job-detail-close"
            >
              <X className="h-4 w-4" />
            </button>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-axor-cyan">
              {job.industry}
            </span>
            <h2 className="mt-2 max-w-md text-3xl font-semibold tracking-tight text-axor-white">
              {job.title}
            </h2>
            <div className="mt-5 flex flex-wrap gap-2 text-xs">
              {[
                [MapPin, job.location],
                [Briefcase, job.type],
                [TrendingUp, job.experience],
                [Building2, job.industry],
              ].map(([Icon, label], i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-medium text-axor-white/75"
                >
                  <Icon className="h-3.5 w-3.5 text-axor-cyan" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-7">
            <p className="text-base leading-relaxed text-axor-white/80">
              {job.summary}
            </p>

            <h3 className="mt-7 text-sm font-bold uppercase tracking-[0.16em] text-axor-cyan">
              What you'll do
            </h3>
            <ul className="mt-4 space-y-3">
              {job.responsibilities.map((r) => (
                <li key={r} className="flex items-start gap-3 text-sm text-axor-white/75">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-axor-turquoise" />
                  {r}
                </li>
              ))}
            </ul>

            <h3 className="mt-7 text-sm font-bold uppercase tracking-[0.16em] text-axor-cyan">
              Skills
            </h3>
            <div className="mt-4 flex flex-wrap gap-2">
              {job.skills.map((s) => (
                <span
                  key={s}
                  className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3 py-1 text-xs font-medium text-axor-white/70"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-white/[0.06] bg-axor-hero/40 p-6 sm:flex-row">
            <AxorButton
              href={waLink(jobApplyMessage(job.title))}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
              icon={WhatsAppGlyph}
              data-testid="job-apply-whatsapp"
            >
              Apply Now
            </AxorButton>
            <AxorButton
              href={mailtoLink(
                `Application: ${job.title}`,
                `Hi Axor Technologies,\n\nI'm interested in the ${job.title} opportunity. Please find my resume attached.\n\nThank you.`,
              )}
              variant="secondary"
              className="flex-1"
              icon={Mail}
              data-testid="job-send-resume"
            >
              Send Resume
            </AxorButton>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
